import type { WorkspaceFileContent } from "@remote-agents/shared";
import { lazy, Suspense, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { formatBytes, formatRelative } from "../format";
const MarkdownViewer = lazy(() => import("./MarkdownViewer").then((module) => ({ default: module.MarkdownViewer })));

export function MarkdownReader({ doc, onCopy }: { doc: WorkspaceFileContent; onCopy: () => void }) {
  const [view, setView] = useState<"rendered" | "raw">("rendered");
  const [fullWidth, setFullWidth] = useState(false);
  const dialogRef = useRef<HTMLDialogElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);
  const expandRef = useRef<HTMLButtonElement>(null);
  const savedScroll = useRef(0);
  const restoreFocus = useRef(false);

  function toggleFullWidth() {
    savedScroll.current = contentRef.current?.scrollTop || 0;
    restoreFocus.current = fullWidth;
    setFullWidth(!fullWidth);
  }

  useLayoutEffect(() => {
    if (contentRef.current) contentRef.current.scrollTop = savedScroll.current;
    if (!fullWidth) {
      if (restoreFocus.current) expandRef.current?.focus({ preventScroll: true });
      restoreFocus.current = false;
      return;
    }
    const dialog = dialogRef.current!;
    const overflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    dialog.showModal();
    // showModal moves focus; restore the reading position after that movement.
    if (contentRef.current) contentRef.current.scrollTop = savedScroll.current;
    return () => {
      dialog.close();
      document.body.style.overflow = overflow;
    };
  }, [fullWidth]);

  const reader = (
    <div className={`md-viewer${fullWidth ? " md-viewer--expanded" : ""}`}>
      <header className="md-viewer__header">
        <div className="md-viewer__bar">
          <p className="md-viewer__path" title={doc.path}>{doc.path}</p>
          <div className="md-viewer__actions">
            <div className="segment segment--sm" role="group" aria-label="View mode">
              <button type="button" className={view === "rendered" ? "is-on" : ""}
                aria-pressed={view === "rendered"} onClick={() => setView("rendered")}>Rendered</button>
              <button type="button" className={view === "raw" ? "is-on" : ""}
                aria-pressed={view === "raw"} onClick={() => setView("raw")}>Raw</button>
            </div>
            <button type="button" className="btn btn--ghost btn--sm" onClick={onCopy}>Copy</button>
            <button ref={expandRef} type="button" className="btn btn--ghost btn--sm"
              aria-label={fullWidth ? "Close full-width reader" : "Full width"}
              onClick={toggleFullWidth}>{fullWidth ? "Close" : "Full width"}</button>
          </div>
        </div>
        <p className="muted md-viewer__meta">{formatBytes(doc.bytes)} · updated {formatRelative(doc.mtime)}</p>
      </header>
      <div ref={contentRef} className="md-viewer__content" role="region" aria-label="File contents" tabIndex={0}>
        {view === "rendered" ? <Suspense fallback={<p className="muted">Rendering document…</p>}><MarkdownViewer source={doc.content} empty="This file is empty." /></Suspense>
          : <pre className="md-raw">{doc.content || "This file is empty."}</pre>}
      </div>
    </div>
  );

  return fullWidth ? createPortal(
    <dialog ref={dialogRef} className="md-dialog" aria-label={`Reading ${doc.path}`}
      onCancel={(event) => { event.preventDefault(); toggleFullWidth(); }}>
      {reader}
    </dialog>, document.body
  ) : reader;
}
